import { getSettingsNavigationItems } from '@/lib/settingsNavigation';
import type { PermissionKey } from '@/lib/permissions';
import type { Role } from '@/types/roles';
import type { WorklynxIconName } from '@/components/icons/iconData';

export type NavigationSection = 'shifts' | 'clients' | 'team' | 'approvals' | 'settings';

export interface SectionNavigationItem {
  id: string;
  label: string;
  path: string;
  icon: WorklynxIconName;
  /**
   * Pages under another prefix that still belong here, so the sidebar stays lit on them.
   * A job's own page is /bo/job/:id, which "/bo/jobs" does not cover.
   */
  relatedPaths?: string[];
  anyPermission?: PermissionKey[];
  requiresCsvImportFeature?: boolean;
}

export interface SectionNavigationAccess {
  canUseCsvImports: boolean;
  hasPermission: (permission: PermissionKey) => boolean;
}

export interface ActiveSectionNavigation {
  section: NavigationSection;
  items: SectionNavigationItem[];
}

export const navigationSections: NavigationSection[] = [
  'shifts',
  'clients',
  'team',
  'approvals',
  'settings',
];

export const navigationSectionLabels: Record<NavigationSection, string> = {
  shifts: 'Shifts',
  clients: 'Clients',
  team: 'Team',
  approvals: 'Approvals',
  settings: 'Settings',
};

export function getSectionNavigationItems(
  section: NavigationSection,
  role: Role | null,
  access: SectionNavigationAccess,
): SectionNavigationItem[] {
  if (section === 'settings') {
    return getSettingsNavigationItems(role, access.hasPermission);
  }

  const basePath = getRoleBasePath(role);
  if (!role || !basePath) return [];

  const items = buildSectionItems(section, role, basePath);

  return items.filter((item) => {
    if (item.requiresCsvImportFeature && !access.canUseCsvImports) return false;
    if (item.anyPermission?.length && !item.anyPermission.some(access.hasPermission)) return false;
    return true;
  });
}

export function getActiveSectionNavigation(
  pathname: string,
  role: Role | null,
  access: SectionNavigationAccess,
): ActiveSectionNavigation | null {
  for (const section of navigationSections) {
    const items = getSectionNavigationItems(section, role, access);
    if (items.some((item) => item.path === pathname)) return { section, items };
  }

  return null;
}

function buildSectionItems(
  section: Exclude<NavigationSection, 'settings'>,
  role: Role,
  basePath: 'bo' | 'bm' | 'manager',
): SectionNavigationItem[] {
  if (section === 'shifts') {
    if (role === 'BM') {
      return [
        {
          id: 'all-shifts',
          label: 'All Shifts',
          path: '/bm/shifts',
          icon: 'calendar',
          anyPermission: ['shifts.view'],
        },
      ];
    }

    if (role !== 'BO' && role !== 'MANAGER') return [];

    return [
      {
        id: 'all-shifts',
        label: 'All Shifts',
        path: `/${basePath}/shifts`,
        icon: 'calendar',
        ...(role === 'MANAGER' ? { anyPermission: ['shifts.view'] as PermissionKey[] } : {}),
      },
      {
        id: 'recurring-series',
        label: 'Recurring Series',
        path: `/${basePath}/recurring-series`,
        icon: 'calendar-add',
        ...(role === 'MANAGER'
          ? { anyPermission: ['shifts.recurring.manage'] as PermissionKey[] }
          : {}),
      },
      {
        id: 'csv-import',
        label: 'CSV Import',
        path: `/${basePath}/shifts/import`,
        icon: 'timesheet',
        anyPermission: ['shifts.csv_import'],
        requiresCsvImportFeature: true,
      },
    ];
  }

  if (section === 'team') {
    if (role !== 'BO' && role !== 'BM' && role !== 'MANAGER') return [];

    const teamPermission = role === 'BO' ? undefined : ['team.view' as PermissionKey];
    const teamItems: SectionNavigationItem[] = [
      {
        id: 'all-members',
        label: 'All Members',
        path: `/${basePath}/team`,
        icon: 'team',
        anyPermission: teamPermission,
      },
    ];

    if (role === 'MANAGER') return teamItems;

    return [
      ...teamItems,
      {
        id: 'employees',
        label: 'Employees',
        path: `/${basePath}/employees`,
        icon: 'user-settings',
        anyPermission: teamPermission,
      },
      {
        id: 'managers',
        label: 'Managers',
        path: `/${basePath}/managers`,
        icon: 'jobs',
        anyPermission: teamPermission,
      },
    ];
  }

  if (section === 'approvals') {
    if (role !== 'BO' && role !== 'BM' && role !== 'MANAGER') return [];

    return [
      {
        id: 'timesheets',
        // Hours are reviewed as pay requests; the heading on the page says the same.
        label: 'Pay Requests',
        path: `/${basePath}/approvals/timesheets`,
        icon: 'time',
        anyPermission: ['timesheets.approve', 'timesheets.reject'],
      },
      {
        id: 'contractor-invoices',
        label: 'Contractor Invoices',
        path: `/${basePath}/approvals/contractor-invoices`,
        icon: 'invoice',
        anyPermission: ['payroll.view'],
      },
      {
        id: 'leave-requests',
        label: 'Leave Requests',
        path: `/${basePath}/approvals/leave`,
        icon: 'calendar',
        anyPermission: [
          'leave.view_team',
          'leave.approve',
          'leave.escalate',
          'leave.coverage.manage',
        ],
      },
    ];
  }

  // The work you do for clients, and what you bill them for it. The organisation's own
  // WorkLynx subscription is not part of this; it lives in Settings.
  if (section === 'clients' && role === 'BO') {
    return [
      { id: 'clients', label: 'Clients', path: '/bo/clients', icon: 'client' },
      { id: 'jobs', label: 'Jobs', path: '/bo/jobs', icon: 'checklist', relatedPaths: ['/bo/job'] },
      { id: 'invoices', label: 'Invoices', path: '/bo/invoices', icon: 'invoice' },
    ];
  }

  return [];
}

function getRoleBasePath(role: Role | null): 'bo' | 'bm' | 'manager' | null {
  if (role === 'BO') return 'bo';
  if (role === 'BM') return 'bm';
  if (role === 'MANAGER') return 'manager';
  return null;
}
